import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, Marker, Polyline, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ArrowRight, ArrowUUpLeft, Crosshair, Pause, Play, Check, Clock, MapPin, MapTrifold,
  DotsThree, Footprints, PuzzlePiece, Ticket, CaretUp, CaretDown, Signpost, Info,
} from '@phosphor-icons/react';
import telegramService from '../../services/telegram';
import BottomSheet from '../BottomSheet';
import MetroMark from '../MetroMark';
import VectorTiles from '../VectorTiles';
import PhotoViewer from './PhotoViewer';
import GeoModals from './GeoModals';
import GeoDebug from './GeoDebug';
import {
  createStopIcon, createMetroIcon, createUserIcon, AttributionPrefix, MapResizer,
  MapFollower, GUIDE_STYLE, CASING_STYLE, AHEAD_STYLE, PASSED_STYLE,
  FRAME_PAD_LEFT, FRAME_PAD_RIGHT, FRAME_PAD_BOTTOM,
} from './mapParts';
import useWalkPaths from '../../hooks/useWalkPaths';
import useNavOffset from '../../hooks/useNavOffset';
import { pushModal, pushBackHandler } from '../../utils/modalStack';
import { getCurrentDayStatus } from '../../utils/workingHours';
import { notify } from '../../utils/notify';
import { plural } from '../../utils/plural';
import { WALK_MAP } from '../../constants/colors';
import {
  formatElapsed, calcDistance, formatDist, WALKABLE_MAX_M, formatWalkTime,
} from '../../utils/walk';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// С какого расстояния переспрашивать при отметке. 300 м — заведомо «не дошёл»:
// погрешность гео в городе редко больше сотни метров
const FAR_THRESHOLD_M = 300;

const PLACES = ['места', 'мест', 'мест'];

// Строка меню места: иконка и подпись, тап-зона 52px
const MenuRow = ({ icon: Icon, onClick, danger, children }) => (
  <button
    onClick={onClick}
    className={`flex min-h-[52px] w-full items-center gap-3 border-0 border-b border-theme-border bg-transparent px-0 text-left text-body last:border-b-0 ${
      danger ? 'text-danger' : 'text-theme-text-primary'}`}
    style={{ WebkitTapHighlightColor: 'transparent' }}
  >
    <Icon size={20} className={`shrink-0 ${danger ? '' : 'text-theme-text-secondary'}`} />
    {children}
  </button>
);

// Окно подтверждения: «Отмена» слева, действие справа
// Необратимые действия (`danger`) тапом мимо не закрываются — слишком легко
const ConfirmSheet = ({ isOpen, onClose, title, action, onAction, danger, children }) => (
  <BottomSheet
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    closeOnBackdrop={!danger}
    footer={
      <div className="flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 h-12 rounded-md text-label transition-colors duration-fast ease-out active:scale-98"
          style={{ background: 'var(--color-card-secondary)', color: 'var(--color-text-secondary)' }}
        >
          Отмена
        </button>
        <button
          onClick={onAction}
          className="flex-1 h-12 rounded-md text-label border-0 transition-colors duration-fast ease-out active:scale-98"
          style={danger
            ? { background: 'var(--color-danger)', color: 'var(--color-on-semantic)' }
            : { background: 'var(--color-action)', color: 'var(--color-on-action)' }}
        >
          {action}
        </button>
      </div>
    }
  >
    <p className="text-body-sm text-theme-text-secondary">{children}</p>
  </BottomSheet>
);

/**
 * Экран прогулки — общий для маршрута редакции и своего маршрута.
 *
 * Карта на весь экран, поверх неё плашка прогресса; снизу панель места:
 * свёрнута — «куда и отметить», развёрнута — как дойти, аудио, задание,
 * рассказ, фото, практическое, весь маршрут. Тап по метке открывает
 * место в панели, не меняя цели.
 *
 * Данные и прогресс держит страница. Сюда приходят места в общем виде:
 * `{ key, title, point, status, thumb, images, description, task,
 *    navigationHint, audio, audioTitle, imagesCaption, entryText,
 *    workingHours, address, slug }`,
 * статус — `done | skipped | active | upcoming`.
 *
 * - `onArrived`, `onSkip` — отметить и пропустить текущее место;
 * - `onBack` — вернуться к предыдущему (нет — пункта в меню нет);
 * - `menuExtra` — свои пункты меню «⋯»: `{ icon, label, onClick, danger }`;
 * - `notice` — плашка над кнопками в шапке панели (подсказки своего
 *   маршрута: «далеко», «закрыто»);
 * - `startStation` — `{ name, lat, lng }`: подсказка «как добраться»
 *   к первому месту и, пока нет геолокации, старт пути на карте.
 */
const WalkScreen = ({
  stops, currentIndex, elapsedMs, geo, startStation,
  onArrived, onSkip, onBack, menuExtra = [], notice,
}) => {
  const navigate = useNavigate();
  const {
    userPosition, userAccuracy, geoBlocked, followMap, setFollowMap,
    handleUserPan, handleLocationBtn,
  } = geo;

  // Место, открытое в панели тапом по метке. null — панель показывает
  // текущую цель. Прогресс и цель от просмотра не меняются
  const [viewIndex, setViewIndex] = useState(null);
  // Нижний край плашки прогресса — верхний отступ кадра карты
  const [pillBottom, setPillBottom] = useState(96);
  const pillRef = useRef(null);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showFarModal, setShowFarModal] = useState(false);
  const [showBackModal, setShowBackModal] = useState(false);
  const [showStopMenu, setShowStopMenu] = useState(false);
  // Панель с местом: свёрнута — только «куда и отметить», развёрнута —
  // как дойти, аудио, задание, рассказ, фото, весь маршрут
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDragDy, setSheetDragDy] = useState(0);
  const [peekHeight, setPeekHeight] = useState(200);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descClamped, setDescClamped] = useState(false);
  const descRef = useRef(null);
  const peekRef = useRef(null);
  const sheetScrollRef = useRef(null);
  const sheetDragRef = useRef(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const mapRef = useRef(null);
  // Сколько снизу занимает нижнее меню и какой высоты экран — от них
  // считаются карта и панель
  const navOffset = useNavOffset();
  const [viewportH, setViewportH] = useState(() => window.innerHeight);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0); // 0–1
  const [audioCurrent, setAudioCurrent] = useState(0);   // секунды
  const [audioDuration, setAudioDuration] = useState(0); // секунды
  const [audioDragging, setAudioDragging] = useState(false);
  // Место, чей рассказ загружен в плеер. Плеер один на экран и не
  // привязан к месту в панели: заглянул вперёд — рассказ продолжает идти
  const [audioIndex, setAudioIndex] = useState(null);
  const audioRef = useRef(null);
  const audioTrackRef = useRef(null);

  const currentStop = stops[currentIndex];
  const shownIndex = viewIndex ?? currentIndex;
  const isViewing = viewIndex !== null && viewIndex !== currentIndex;
  const shownStop = stops[shownIndex];
  const targetLatLng = currentStop?.point || null;
  const passedCount = stops.filter((s) => s.status === 'done' || s.status === 'skipped').length;

  // Станция у первого места: подсказка «как добраться» — пока маршрут
  // не начат; стартом пути на карте — только без геолокации
  const firstStation = currentIndex === 0 && passedCount === 0 ? startStation : null;
  const stationPoint = firstStation && !userPosition ? [firstStation.lat, firstStation.lng] : null;

  const {
    passedLine, legLine, aheadLine, guideLine: shownGuide, guideIsRoute, stationDistance,
  } = useWalkPaths({
    points: stops.map((s) => s.point),
    currentIndex,
    userPosition,
    stationPoint,
  });
  const stationWalk = stationDistance !== null && stationDistance <= WALKABLE_MAX_M
    ? formatWalkTime(stationDistance)?.replace(' пешком', '')
    : null;

  // Высота экрана — от неё считается развёрнутая панель
  useEffect(() => {
    const measure = () => setViewportH(window.innerHeight);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Высота шапки панели: от неё зависит, где кончается карта. Состав
  // шапки меняется от места к месту — название в одну или две строки
  useEffect(() => {
    const node = peekRef.current;
    if (!node) return undefined;
    const measure = () => setPeekHeight(node.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Высота плашки прогресса — верхний отступ кадра карты: метки
  // не должны прятаться под ней
  useEffect(() => {
    const node = pillRef.current;
    if (!node) return undefined;
    const measure = () => setPillBottom(Math.round(node.getBoundingClientRect().bottom) + 12);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Сброс подробностей при смене показанного места — и при переходе
  // к следующему, и при просмотре другого с карты. Плеер не трогаем:
  // раньше рассказ обрывался, стоило заглянуть на другое место
  useEffect(() => {
    setDescExpanded(false);
    if (sheetScrollRef.current) sheetScrollRef.current.scrollTop = 0;
    setLightboxIndex(null);
  }, [shownIndex]);

  // «Читать полностью» — только если рассказ действительно обрезан.
  // Панель свёрнута сдвигом, а не скрыта, поэтому замер работает и в ней
  useLayoutEffect(() => {
    const node = descRef.current;
    if (!node) { setDescClamped(false); return; }
    if (!descExpanded) setDescClamped(node.scrollHeight > node.clientHeight + 1);
  }, [shownIndex, descExpanded, stops]);

  // Новая цель: просмотр другого места закрывается, панель сворачивается
  // к «куда идти», карту снова ведём сами — иначе она осталась бы там,
  // где человек её последний раз оставил
  useEffect(() => {
    setViewIndex(null);
    setSheetOpen(false);
    setFollowMap(true);
  }, [currentIndex, setFollowMap]);

  // Фото на весь экран — тоже окно: «Назад» Telegram и Escape закрывают
  // его, а не уводят с прогулки (брендбук 7.5)
  const lightboxOpen = lightboxIndex !== null;
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const close = () => setLightboxIndex(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const unregister = pushModal(close);
    return () => {
      document.removeEventListener('keydown', onKey);
      unregister();
    };
  }, [lightboxOpen]);

  // «Назад» Telegram шаг за шагом: из просмотра другого места — к цели,
  // из развёрнутой панели — в свёрнутую, и только потом со страницы
  useEffect(() => {
    if (!sheetOpen && !isViewing) return undefined;
    return pushBackHandler(() => {
      if (isViewing) {
        setViewIndex(null);
        setFollowMap(true);
      } else {
        setSheetOpen(false);
      }
      return true;
    });
  }, [sheetOpen, isViewing, setFollowMap]);

  if (!shownStop) return null;

  const hoursStatus = shownStop.workingHours ? getCurrentDayStatus(shownStop.workingHours) : null;
  const stopImages = shownStop.images?.length ? shownStop.images : null;
  const initialCenter = targetLatLng || stops.find((s) => s.point)?.point || [59.9343, 30.3351];

  // Сколько ещё идти до текущей точки. Считается по прямой, поэтому
  // «~» — пешком всегда выходит больше
  const distanceToTarget = (targetLatLng && userPosition)
    ? calcDistance(userPosition[0], userPosition[1], targetLatLng[0], targetLatLng[1])
    : null;
  const distanceLabel = distanceToTarget !== null ? formatDist(distanceToTarget) : null;
  // Карта действительно едет за пользователем: слежение включено
  // и позиция известна
  const isFollowing = followMap && !!userPosition && !geoBlocked;
  // Расстояние до места в панели. У просматриваемого — своё, по прямой
  const distanceToShown = isViewing && shownStop.point && userPosition
    ? calcDistance(userPosition[0], userPosition[1], shownStop.point[0], shownStop.point[1])
    : distanceToTarget;
  const shownDistanceLabel = distanceToShown !== null ? formatDist(distanceToShown) : null;
  // Пешеход идёт ~4.5 км/ч. Дальше 10 км время не показываем: столько
  // пешком не ходят, а «1657 мин» это не подсказка
  const shownEtaLabel = (distanceToShown !== null && distanceToShown <= WALKABLE_MAX_M)
    ? formatWalkTime(distanceToShown)
    : null;

  // Тап по метке: открыть место в панели. По цели — вернуться к ней
  const selectStop = (i) => {
    telegramService.hapticFeedback('light');
    if (i === currentIndex) {
      setViewIndex(null);
      setFollowMap(true);
      return;
    }
    setViewIndex(i);
    setFollowMap(false);
    const pt = stops[i]?.point;
    if (pt && mapRef.current) mapRef.current.panTo(pt, { animate: true });
  };
  const backToTarget = () => {
    telegramService.hapticFeedback('light');
    setViewIndex(null);
    setFollowMap(true);
  };

  // ── Геометрия экрана ──
  // Карта занимает всё от верха до шапки панели. Развёрнутая панель
  // встаёт поверх карты, оставляя сверху полосу с прогрессом
  const sheetTopGap = 96;
  const sheetFullH = Math.max(peekHeight, viewportH - navOffset - sheetTopGap);
  const sheetCollapsedY = Math.max(0, sheetFullH - peekHeight);
  const sheetY = Math.min(
    sheetCollapsedY,
    Math.max(0, (sheetOpen ? 0 : sheetCollapsedY) + sheetDragDy)
  );
  const mapBottom = navOffset + peekHeight;

  /**
   * Панель тянется за шапку, тапом по «Подробнее» — переключается.
   * Слушатели вешаются на window: захват указателя на шапке отбирал бы
   * клики у кнопок внутри неё. Жест по кнопке панель не двигает.
   *
   * Свайп Telegram «свернуть приложение» не мешает: вертикальные свайпы
   * отключены в telegram.js (`disableVerticalSwipes`), брендбук 7.4
   */
  const onSheetPointerDown = (e) => {
    if (e.button > 0 || e.target.closest('button, a')) return;
    sheetDragRef.current = { startY: e.clientY, moved: false, t: Date.now() };

    const onMove = (me) => {
      const d = sheetDragRef.current;
      if (!d) return;
      const dy = me.clientY - d.startY;
      if (!d.moved && Math.abs(dy) > 6) d.moved = true;
      if (d.moved) setSheetDragDy(dy);
    };
    const onUp = (ue) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const d = sheetDragRef.current;
      sheetDragRef.current = null;
      setSheetDragDy(0);
      if (!d) return;
      const dy = ue.clientY - d.startY;
      const velocity = dy / Math.max(1, Date.now() - d.t);
      let next = sheetOpen;
      if (!d.moved) next = !sheetOpen;
      else if (dy < -40 || velocity < -0.4) next = true;
      else if (dy > 40 || velocity > 0.4) next = false;
      if (next !== sheetOpen) {
        telegramService.hapticFeedback('light');
        setSheetOpen(next);
        if (!next && sheetScrollRef.current) sheetScrollRef.current.scrollTop = 0;
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  // Весь маршрут в кадре — обзор. Слежение снимается: иначе карта
  // через секунду вернулась бы к пользователю
  const showWholeRoute = () => {
    const map = mapRef.current;
    const pts = stops.map((s) => s.point).filter(Boolean);
    if (userPosition) pts.push(userPosition);
    if (!map || pts.length === 0) return;
    telegramService.hapticFeedback('light');
    setFollowMap(false);
    // Сверху — отступ под плашку прогресса: раньше метки прятались под ней
    map.fitBounds(pts, {
      paddingTopLeft: [FRAME_PAD_LEFT, pillBottom],
      paddingBottomRight: [FRAME_PAD_RIGHT, FRAME_PAD_BOTTOM],
      maxZoom: 16,
    });
  };

  const markArrived = () => {
    telegramService.hapticFeedback('medium');
    setShowFarModal(false);
    // Отметка — событие: раньше контент просто молча подменялся,
    // и было непонятно, засчиталось ли. Отступ снизу — на высоту шапки
    // панели: иначе тост ложился поверх «Я на месте». Текст белым:
    // в тёмной теме on-semantic почти чёрный, а заливка зелёная
    if (currentIndex + 1 < stops.length) {
      notify.success(`Место ${currentIndex + 1} из ${stops.length} пройдено`, {
        style: { color: '#fff', marginBottom: peekHeight },
        iconColor: '#fff',
      });
    }
    onArrived();
  };

  const onArrivedClick = () => {
    // Заведомо далеко — переспрашиваем. Не запрет: гео может
    // врать, а точка бывает вписана вручную с неточными
    // координатами. Просто «вы уверены?»
    if (distanceToTarget !== null && distanceToTarget > FAR_THRESHOLD_M) {
      telegramService.hapticFeedback('light');
      setShowFarModal(true);
    } else {
      markArrived();
    }
  };

  // Из меню — следующая панель открывается, когда эта уже ушла:
  // стопка панелей запрещена (брендбук 7.6)
  const fromStopMenu = (action) => {
    telegramService.hapticFeedback('light');
    setShowStopMenu(false);
    setTimeout(action, 220);
  };

  const formatAudioTime = (sec) => {
    const s = Math.floor(sec || 0);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  // Пауза / продолжить — то, что загружено в плеере
  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) return;
    if (audioPlaying) el.pause();
    else el.play().catch(() => setAudioPlaying(false));
  };
  /**
   * «Слушать» на карточке места. Если в плеере рассказ другого места —
   * подменяем источник прямо в обработчике тапа: iOS разрешает play()
   * только внутри жеста, а запуск по onLoadedMetadata после смены `src`
   * через состояние был бы уже вне его
   */
  const playStopAudio = (i) => {
    const el = audioRef.current;
    const url = stops[i]?.audio?.url;
    if (!el || !url) return;
    if (audioIndex !== i) {
      el.src = url;
      setAudioIndex(i);
      setAudioCurrent(0);
      setAudioProgress(0);
      setAudioDuration(0);
      el.play().catch(() => setAudioPlaying(false));
      return;
    }
    toggleAudio();
  };
  const seekAudioBy = (sec) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    el.currentTime = Math.max(0, Math.min(el.duration, el.currentTime + sec));
    setAudioCurrent(el.currentTime);
    setAudioProgress(el.currentTime / el.duration);
  };
  // Перемотка пальцем по дорожке: одна функция на мышь и касание
  const startAudioSeek = (clientX, isTouch) => {
    const track = audioTrackRef.current;
    const el = audioRef.current;
    if (!track || !el) return;
    const seek = (x) => {
      const rect = track.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (x - rect.left) / rect.width));
      el.currentTime = ratio * (el.duration || 0);
      setAudioProgress(ratio);
      setAudioCurrent(el.currentTime);
    };
    setAudioDragging(true);
    seek(clientX);
    const moveEvt = isTouch ? 'touchmove' : 'mousemove';
    const endEvt = isTouch ? 'touchend' : 'mouseup';
    const onMove = (ev) => seek(isTouch ? ev.touches[0].clientX : ev.clientX);
    const onEnd = () => {
      setAudioDragging(false);
      window.removeEventListener(moveEvt, onMove);
      window.removeEventListener(endEvt, onEnd);
    };
    window.addEventListener(moveEvt, onMove, isTouch ? { passive: true } : undefined);
    window.addEventListener(endEvt, onEnd);
  };

  // Рассказ включён (играет или на паузе посередине) — в шапке панели
  // кнопка паузы: свернул панель и пошёл, а остановить можно без неё
  const audioActive = audioIndex !== null && (audioPlaying || (audioCurrent > 0 && audioProgress < 1));
  const audioShownLoaded = audioIndex === shownIndex;

  return (
    <div className="fixed inset-0 overflow-hidden bg-theme-bg">

      {/* Плеер аудиогида — один на экран, источник ставит playStopAudio */}
      <audio
        ref={audioRef}
        preload="metadata"
        onLoadedMetadata={(e) => setAudioDuration(e.target.duration)}
        onTimeUpdate={(e) => {
          const el = e.target;
          setAudioCurrent(el.currentTime);
          setAudioProgress(el.duration ? el.currentTime / el.duration : 0);
        }}
        onPlay={() => setAudioPlaying(true)}
        onPause={() => setAudioPlaying(false)}
        onEnded={(e) => { setAudioPlaying(false); setAudioProgress(1); setAudioCurrent(e.target.duration || 0); }}
      />

      {/* ── Карта ── от верха экрана до шапки панели */}
      <div className="absolute inset-x-0 top-0 z-0" style={{ bottom: mapBottom }}>
        <MapContainer
          center={initialCenter}
          zoom={16}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          ref={mapRef}
        >
          <AttributionPrefix />
          <VectorTiles />
          <MapResizer height={mapBottom} />
          {/* Без геолокации опора кадра — место, где человек только что
              нажал «Я на месте»: он стоит там. Раньше карта центрировалась
              на одной цели и перескакивала к ней, теряя, откуда идти */}
          <MapFollower
            userPosition={userPosition || (currentIndex > 0 ? stops[currentIndex - 1]?.point : stationPoint)}
            target={targetLatLng}
            follow={followMap}
            onUserPan={handleUserPan}
            padTop={pillBottom}
          />

          {/* Линии снизу вверх: позади → впереди → к цели. Сплошные:
              пунктир в картах значит «необязательно», а это и есть путь */}
          {passedLine && (
            <Polyline positions={passedLine} pathOptions={PASSED_STYLE} interactive={false} />
          )}
          {/* Впереди — точками, как пешеходный маршрут в картах: «это потом» */}
          {aheadLine && (
            <Polyline positions={aheadLine} pathOptions={AHEAD_STYLE} interactive={false} />
          )}
          {/* Отрезок от прошлого места к цели. Без геолокации он и есть
              «куда идти» — главной линией; с ней главная — путь от меня,
              а отрезок остаётся точками, как остальное впереди */}
          {legLine && (
            <>
              {!shownGuide && (
                <Polyline positions={legLine} pathOptions={CASING_STYLE} interactive={false} />
              )}
              <Polyline
                positions={legLine}
                pathOptions={shownGuide ? AHEAD_STYLE : GUIDE_STYLE}
                interactive={false}
              />
            </>
          )}

          {/* Путь «сейчас» — главная линия: толще всех и с белой обводкой,
              чтобы не терялась на белых улицах подложки */}
          {shownGuide && (
            <>
              <Polyline positions={shownGuide} pathOptions={CASING_STYLE} interactive={false} />
              <Polyline
                positions={shownGuide}
                // По прямой — штрихами: это не путь, а направление.
                // Не точками — точки значат «впереди по маршруту»
                pathOptions={guideIsRoute ? GUIDE_STYLE : { ...GUIDE_STYLE, dashArray: '12 10' }}
                interactive={false}
              />
            </>
          )}

          {/* Круг точности: в городе GPS ошибается на десятки метров,
              и без круга «я в доме» выглядит как поломка */}
          {userPosition && userAccuracy > 15 && userAccuracy < 500 && (
            <Circle
              center={userPosition}
              radius={userAccuracy}
              pathOptions={{ stroke: false, fillColor: WALK_MAP.ahead, fillOpacity: 0.1 }}
              interactive={false}
            />
          )}

          {/* Метки. Тап открывает место в панели */}
          {stops.map((stop, i) => {
            if (!stop.point) return null;
            const kind = i === currentIndex
              ? 'target'
              : i === viewIndex
                ? 'viewed'
                : stop.status === 'done' || stop.status === 'skipped'
                  ? stop.status
                  : 'upcoming';
            // Подпись — у одного пина: пока открыт просмотр, у просматриваемого.
            // Цель и просматриваемое место бывают рядом, и две подписи
            // налезали друг на друга
            const title = kind === 'viewed' || (kind === 'target' && !isViewing) ? stop.title : null;
            return (
              <Marker
                key={stop.key}
                position={stop.point}
                icon={createStopIcon(i, kind, title)}
                zIndexOffset={kind === 'target' ? 1000 : kind === 'viewed' ? 900 : 0}
                eventHandlers={{ click: () => selectStop(i) }}
                keyboard={false}
                title={stop.title || `Место ${i + 1}`}
              />
            );
          })}
          {stationPoint && (
            <Marker
              position={stationPoint}
              icon={createMetroIcon(`м. ${firstStation.name}`)}
              zIndexOffset={800}
              interactive={false}
              keyboard={false}
            />
          )}
          {userPosition && (
            <Marker
              position={userPosition}
              icon={createUserIcon()}
              zIndexOffset={1100}
              interactive={false}
              keyboard={false}
            />
          )}
        </MapContainer>

        {/* Прогресс поверх карты: сколько пройдено и сколько в пути.
            Отступ — от врезки устройства и кнопок Telegram */}
        <div
          ref={pillRef}
          className="pointer-events-none absolute inset-x-4 z-[500] mx-auto max-w-md"
          // +4px, а не +12: Telegram уже кладёт в content-safe-area-top
          // зазор под своими кнопками, и плашка висела слишком низко
          style={{ top: 'calc(var(--safe-area-top, 0px) + var(--content-safe-area-top, 0px) + 4px)' }}
        >
          <div className="rounded-lg border border-theme-border bg-theme-card px-4 py-3">
            <div className="flex items-center justify-between gap-3 text-body-sm tabular">
              <span className="text-theme-text-primary">
                {passedCount} из {stops.length} {plural(stops.length, PLACES)}
              </span>
              <span className="flex items-center gap-1.5 text-theme-text-secondary">
                <Clock size={16} />
                {/* В первую минуту — «только начали»: «меньше минуты»
                    в плашке прогресса звучало странно */}
                {elapsedMs < 60000 ? 'только начали' : formatElapsed(elapsedMs)}
              </span>
            </div>
            {/* Один цвет разной интенсивности: пройденное — сплошной синий,
                текущее — приглушённый, остальное — фон */}
            <div className="mt-2 flex gap-1">
              {stops.map((stop, i) => (
                <div
                  key={stop.key}
                  className="h-1 flex-1 rounded-full transition-colors duration-base"
                  style={{
                    background: stop.status === 'done' || stop.status === 'skipped'
                      ? 'var(--color-action)'
                      : i === currentIndex
                        ? 'var(--color-action-tint)'
                        : 'var(--color-border)',
                    opacity: stop.status === 'skipped' ? 0.4 : 1,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Кнопки карты: где я и весь маршрут. 44px, подложка по теме */}
        <div className="absolute bottom-3 right-3 z-[500] flex flex-col gap-2">
          <button
            onClick={showWholeRoute}
            aria-label="Показать весь маршрут"
            className="flex h-11 w-11 items-center justify-center rounded-md border border-theme-border bg-theme-card text-theme-text-primary"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <MapTrifold size={20} />
          </button>
          {/* Геолокация. Залитая иконка — «карта едет за вами»,
              контурная — «слежение снято, нажми, чтобы вернуть» */}
          <button
            onClick={handleLocationBtn}
            aria-pressed={isFollowing}
            aria-label={isFollowing ? 'Карта следит за вами' : 'Показать моё местоположение'}
            className="flex h-11 w-11 items-center justify-center rounded-md border border-theme-border bg-theme-card"
            style={{
              color: isFollowing ? 'var(--color-action)' : 'var(--color-text-secondary)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Crosshair size={20} weight={isFollowing ? 'fill' : 'regular'} />
          </button>
        </div>
      </div>

      {/* Затемнение под развёрнутой панелью: тап по карте сворачивает её */}
      {sheetOpen && (
        <div
          className="absolute inset-0 z-[15]"
          style={{ bottom: navOffset }}
          onClick={() => setSheetOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Панель места ──
          Высота постоянная, видимая часть задаётся сдвигом: анимируется
          только transform (брендбук 11) */}
      <div
        className="absolute inset-x-0 z-20 mx-auto flex max-w-md flex-col rounded-t-2xl border border-b-0 border-theme-border bg-theme-card"
        style={{
          bottom: navOffset,
          height: sheetFullH,
          transform: `translateY(${sheetY}px)`,
          transition: sheetDragDy ? 'none' : 'transform var(--dur-slow, 320ms) cubic-bezier(.2,0,0,1)',
        }}
      >
        {/* Шапка: всегда видна. Тянется и переключается тапом */}
        <div ref={peekRef} className="shrink-0 px-4 pb-4">
          <div
            onPointerDown={onSheetPointerDown}
            className="cursor-grab select-none"
            style={{ touchAction: 'none' }}
          >
            {/* Вместо немой серой ручки — кнопка с подписью: «Подробнее»
                и «Скрыть» понятнее полоски. Свайп работает как раньше:
                кнопка не во всю ширину, тянуть можно по бокам от неё
                и за шапку. Стрелка «Подробнее» подпрыгивает при каждом
                заходе — этого хватает, подъём всей панели убран */}
            <div className="flex justify-center">
              <button
                onClick={() => {
                  telegramService.hapticFeedback('light');
                  setSheetOpen((v) => !v);
                }}
                aria-expanded={sheetOpen}
                // Текстом, без подложки и рамки — плашка с обводкой
                // выглядела грубо. Акцент — стрелка в синем кружке
                className="flex h-10 items-center gap-2 rounded-full border-0 bg-transparent px-4 text-label text-action-text"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                  style={{ background: 'var(--color-action-tint)' }}
                >
                  {sheetOpen
                    ? <CaretDown size={14} weight="bold" />
                    : <CaretUp size={14} weight="bold" className="animate-nudgeUp" />}
                </span>
                {sheetOpen ? 'Скрыть' : 'Подробнее'}
              </button>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <div
                className="w-12 shrink-0 overflow-hidden rounded-md bg-theme-card-secondary"
                style={{ aspectRatio: '3 / 4' }}
              >
                {shownStop.thumb && <img src={shownStop.thumb} alt="" className="h-full w-full object-cover" draggable={false} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-caption text-theme-text-secondary tabular">
                  {isViewing
                    ? `Место ${shownIndex + 1} из ${stops.length} · ${
                      shownStop.status === 'done' ? 'пройдено'
                        : shownStop.status === 'skipped' ? 'пропущено'
                          : 'впереди'}`
                    : currentIndex === 0 ? 'Старт' : `Место ${currentIndex + 1} из ${stops.length}`}
                </div>
                <div className="line-clamp-2 font-display text-h3 text-theme-text-primary">
                  {shownStop.title || `Место ${shownIndex + 1}`}
                </div>
                {/* На старте — как добраться до первого места */}
                {!isViewing && firstStation && (
                  <div className="mt-1 flex min-w-0 items-center gap-1.5 text-body-sm text-theme-text-secondary tabular">
                    <MetroMark />
                    {/* Значок «М» уже говорит, что это метро. Без гео —
                        ещё и сколько идти от станции */}
                    <span className="truncate">
                      {firstStation.name}
                      {!userPosition && stationDistance !== null && ` · ${formatDist(stationDistance)}`}
                      {!userPosition && stationWalk && ` · ${stationWalk}`}
                    </span>
                  </div>
                )}
                {/* Сколько ещё идти — главный вопрос на ходу */}
                {shownDistanceLabel && (
                  <div className="mt-1 flex items-center gap-1.5 text-body-sm text-action-text tabular">
                    <Footprints size={16} className="shrink-0" />
                    {shownDistanceLabel}{shownEtaLabel && ` · ${shownEtaLabel}`}
                  </div>
                )}
              </div>
              {/* Аудиогид прямо в шапке: у места есть рассказ — кнопка
                  видна сразу. Пока рассказ идёт — пауза, даже если
                  в панели открыто другое место */}
              {(audioActive || shownStop.audio?.url) && (
                <button
                  onClick={audioActive ? toggleAudio : () => playStopAudio(shownIndex)}
                  aria-label={audioPlaying ? 'Пауза аудиогида' : audioActive ? 'Продолжить аудиогид' : 'Слушать аудиогид'}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-0 bg-theme-bg-secondary text-action-text transition-transform duration-fast ease-out active:scale-97"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {audioPlaying ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
                </button>
              )}
            </div>
          </div>

          {/* Подсказка страницы — «далеко», «закрыто» */}
          {!isViewing && notice}

          {/* Без геолокации — прямое предложение её включить */}
          {!shownDistanceLabel && (
            <button
              onClick={handleLocationBtn}
              className="mt-3 flex h-10 items-center gap-2 rounded-full border-0 bg-theme-bg-secondary px-3 text-body-sm text-action-text"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Crosshair size={16} className="shrink-0" />
              Показать, сколько идти
            </button>
          )}

          {/* Просмотр другого места: отметить его нельзя — порядок
              маршрута не ломаем. Единственное действие — назад к цели */}
          {isViewing ? (
            <div className="mt-3">
              <button
                onClick={backToTarget}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-md border-0 bg-action px-4 text-label text-on-action transition-transform duration-fast ease-out active:scale-98"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <ArrowUUpLeft size={20} className="shrink-0" />
                <span className="min-w-0 truncate">
                  {`К месту ${currentIndex + 1}: ${currentStop?.title || 'текущая цель'}`}
                </span>
              </button>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              {/* Основное действие экрана — синее, всегда на виду */}
              <button
                onClick={onArrivedClick}
                className="flex h-14 flex-1 items-center justify-center gap-2 rounded-md border-0 bg-action text-label text-on-action transition-transform duration-fast ease-out active:scale-98"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Check size={20} weight="bold" />
                Я на месте
              </button>
              {/* Пропустить, вернуться и прочее — в меню: на ходу они нужны
                  редко и не должны спорить с главной */}
              <button
                onClick={() => { telegramService.hapticFeedback('light'); setShowStopMenu(true); }}
                aria-label="Другие действия"
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border-0 bg-theme-card-secondary text-theme-text-primary transition-transform duration-fast ease-out active:scale-98"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <DotsThree size={24} weight="bold" />
              </button>
            </div>
          )}
        </div>

        {/* ── Подробности: прокручиваются внутри развёрнутой панели ── */}
        <div
          ref={sheetScrollRef}
          className="min-h-0 flex-1 border-t border-theme-border px-4 pb-8 pt-5"
          style={{ overflowY: sheetOpen ? 'auto' : 'hidden', overscrollBehavior: 'contain' }}
        >
          {/* Как дойти — первым: это то, что нужно прямо сейчас */}
          {shownStop.navigationHint && (
            <section className="mb-6">
              <h3 className="mb-2 flex items-center gap-2 font-display text-h3 text-theme-text-primary">
                <Signpost size={20} className="text-theme-text-secondary" />
                Как дойти
              </h3>
              <p className="m-0 text-body text-theme-text-primary">{shownStop.navigationHint}</p>
            </section>
          )}

          {/* Аудиогид: крупная кнопка, перемотка на 15 секунд */}
          {shownStop.audio?.url && (
            <section className="mb-6 rounded-lg bg-theme-bg-secondary p-4">
              <div className="text-caption text-theme-text-secondary">Аудиогид</div>
              <div className="text-label text-theme-text-primary">
                {shownStop.audioTitle || shownStop.title || 'Рассказ о месте'}
              </div>

              {/* Дорожка. Тап-зона 24px при видимой полосе 4px. Пока
                  в плеере рассказ другого места — дорожка пустая */}
              <div
                ref={audioShownLoaded ? audioTrackRef : undefined}
                className="relative mt-3 flex h-6 cursor-pointer items-center"
                onMouseDown={(e) => audioShownLoaded && startAudioSeek(e.clientX, false)}
                onTouchStart={(e) => audioShownLoaded && startAudioSeek(e.touches[0].clientX, true)}
              >
                <div className="absolute inset-x-0 h-1 rounded-full bg-theme-border" />
                {/* Заливка и бегунок движутся через transform, а не width
                    и left (брендбук 11) */}
                <div
                  className="absolute inset-x-0 h-1 origin-left rounded-full bg-action"
                  style={{
                    transform: `scaleX(${audioShownLoaded ? audioProgress : 0})`,
                    transition: audioDragging ? 'none' : 'transform 0.25s linear',
                  }}
                />
                <div
                  className="pointer-events-none absolute inset-x-0"
                  style={{
                    transform: `translateX(${(audioShownLoaded ? audioProgress : 0) * 100}%)`,
                    transition: audioDragging ? 'none' : 'transform 0.25s linear',
                  }}
                >
                  <div className="-ml-2 h-4 w-4 rounded-full border-2 border-theme-card bg-action" />
                </div>
              </div>
              <div className="flex justify-between text-caption text-theme-text-secondary tabular">
                <span>{formatAudioTime(audioShownLoaded ? audioCurrent : 0)}</span>
                <span>{audioShownLoaded && audioDuration ? formatAudioTime(audioDuration) : ''}</span>
              </div>

              <div className="mt-2 flex items-center justify-center gap-6">
                <button
                  onClick={() => seekAudioBy(-15)}
                  disabled={!audioShownLoaded}
                  aria-label="Назад на 15 секунд"
                  className="flex h-11 w-11 items-center justify-center rounded-full border-0 bg-transparent text-label text-theme-text-primary tabular disabled:opacity-40"
                >
                  −15
                </button>
                <button
                  onClick={() => playStopAudio(shownIndex)}
                  aria-label={audioShownLoaded && audioPlaying ? 'Пауза' : 'Слушать'}
                  className="flex h-14 w-14 items-center justify-center rounded-full border-0 bg-action text-on-action transition-transform duration-fast ease-out active:scale-97"
                >
                  {audioShownLoaded && audioPlaying ? <Pause size={24} weight="fill" /> : <Play size={24} weight="fill" />}
                </button>
                <button
                  disabled={!audioShownLoaded}
                  onClick={() => seekAudioBy(15)}
                  aria-label="Вперёд на 15 секунд"
                  className="flex h-11 w-11 items-center justify-center rounded-full border-0 bg-transparent text-label text-theme-text-primary tabular disabled:opacity-40"
                >
                  +15
                </button>
              </div>
            </section>
          )}

          {/* Задание — отдельной карточкой: это то, что отличает прогулку
              от чтения путеводителя */}
          {shownStop.task && (
            <section className="mb-6 flex gap-3 rounded-lg border border-theme-border p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-lime">
                <PuzzlePiece size={20} weight="fill" color="#16181D" />
              </div>
              <div className="min-w-0">
                <div className="text-label text-theme-text-primary">Задание</div>
                <p className="m-0 mt-1 text-body text-theme-text-primary">{shownStop.task}</p>
              </div>
            </section>
          )}

          {shownStop.description && (
            <section className="mb-6">
              <h3 className="mb-2 font-display text-h3 text-theme-text-primary">О месте</h3>
              <p
                ref={descRef}
                className={`m-0 whitespace-pre-line text-body text-theme-text-primary ${descExpanded ? '' : 'line-clamp-5'}`}
              >
                {shownStop.description}
              </p>
              {/* По факту обрезки, а не по числу символов */}
              {!descExpanded && descClamped && (
                <button
                  onClick={() => setDescExpanded(true)}
                  className="mt-1 h-11 border-0 bg-transparent p-0 text-label text-action-text"
                >
                  Читать полностью
                </button>
              )}
            </section>
          )}

          {/* Фотографии — вертикальные 3:4, как во всём приложении.
              Тап открывает снимок целиком */}
          {stopImages && (
            <section className="mb-6">
              <div className="-mx-4 flex gap-2 overflow-x-auto px-4" style={{ scrollbarWidth: 'none' }}>
                {stopImages.map((img, i) => (
                  <button
                    key={img.id ?? i}
                    onClick={() => { telegramService.hapticFeedback('light'); setLightboxIndex(i); }}
                    aria-label={`Фото ${i + 1}`}
                    className="w-[132px] shrink-0 overflow-hidden rounded-lg border-0 bg-theme-card-secondary p-0"
                    style={{ aspectRatio: '4 / 5', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <img src={img.url} alt="" loading="lazy" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
              {shownStop.imagesCaption && (
                <p className="m-0 mt-2 text-body-sm text-theme-text-secondary">{shownStop.imagesCaption}</p>
              )}
            </section>
          )}

          {/* Практическое: вход, часы, адрес — строками с иконками */}
          {(shownStop.entryText || hoursStatus || shownStop.address) && (
            <section className="mb-6 flex flex-col gap-3">
              {shownStop.entryText && (
                <div className="flex items-center gap-3 text-body text-theme-text-primary">
                  <Ticket size={20} className="shrink-0 text-theme-text-secondary" />
                  <span>Вход: {shownStop.entryText}</span>
                </div>
              )}
              {hoursStatus && (
                <div className="flex items-center gap-3 text-body">
                  <Clock size={20} className="shrink-0 text-theme-text-secondary" />
                  <span style={{ color: hoursStatus.color }}>{hoursStatus.message}</span>
                </div>
              )}
              {shownStop.address && (
                <div className="flex items-center gap-3 text-body text-theme-text-primary">
                  <MapPin size={20} className="shrink-0 text-theme-text-secondary" />
                  <span>{shownStop.address}</span>
                </div>
              )}
            </section>
          )}

          {/* Перехода во внешние карты нет намеренно: путь до места
              ведёт своя карта на этом экране */}
          {shownStop.slug && (
            <div className="mb-8 flex gap-2">
              <button
                onClick={() => navigate(`/place/${shownStop.slug}`)}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md border-0 bg-theme-card-secondary text-label text-action-text"
              >
                <Info size={20} />
                О месте
              </button>
            </div>
          )}

          {/* ── Весь маршрут ── статус значком у номера */}
          <section>
            <h3 className="mb-2 font-display text-h3 text-theme-text-primary">Весь маршрут</h3>
            <ol className="m-0 list-none p-0">
              {stops.map((stop, i) => {
                const isCurrent = i === currentIndex;
                const isShown = i === shownIndex;
                const isPassed = stop.status === 'done' || stop.status === 'skipped';
                return (
                  <li key={stop.key} className="border-b border-theme-border last:border-b-0">
                    {/* Открытое в панели место — плашкой со своими полями
                        и скруглением, не впритык к разделителям */}
                    <button
                      onClick={() => selectStop(i)}
                      className={`-mx-2 my-1 flex min-h-[48px] w-[calc(100%+16px)] items-center gap-3 rounded-md border-0 px-2 text-left transition-colors duration-fast ${
                        isShown && isViewing ? 'bg-theme-bg-secondary' : 'bg-transparent'}`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {/* Кружки — как метки на карте: цель синяя,
                          впереди белые с рамкой, позади серые */}
                      <div
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-kicker tabular"
                        style={isCurrent
                          ? { background: 'var(--color-action)', color: 'var(--color-on-action)' }
                          : stop.status === 'done'
                            ? { background: 'var(--color-text-hint)', color: 'var(--color-card)' }
                            : {
                              background: 'var(--color-card)',
                              color: stop.status === 'skipped' ? 'var(--color-text-hint)' : 'var(--color-text-primary)',
                              border: `1.5px solid ${stop.status === 'skipped' ? 'var(--color-text-hint)' : 'var(--color-border-strong)'}`,
                            }}
                      >
                        {stop.status === 'done'
                          ? <Check size={16} weight="bold" />
                          : stop.status === 'skipped'
                            ? <ArrowRight size={16} weight="bold" />
                            : i + 1}
                      </div>
                      <span
                        className={`min-w-0 flex-1 truncate text-body ${isCurrent ? 'font-semibold text-theme-text-primary' : isPassed ? 'text-theme-text-secondary' : 'text-theme-text-primary'}`}
                      >
                        {stop.title || `Место ${i + 1}`}
                      </span>
                      <span className="shrink-0 text-caption text-theme-text-secondary">
                        {isCurrent ? 'сейчас' : stop.status === 'done' ? 'пройдено' : stop.status === 'skipped' ? 'пропущено' : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        </div>
      </div>

      {/* Просмотр фотографий места — листаются свайпом */}
      {lightboxIndex !== null && stopImages?.[lightboxIndex] && (
        <PhotoViewer
          images={stopImages}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Меню: редкие действия с местом и маршрутом */}
      <BottomSheet
        isOpen={showStopMenu}
        onClose={() => setShowStopMenu(false)}
        title={currentStop?.title || `Место ${currentIndex + 1}`}
      >
        <div className="flex flex-col">
          <MenuRow icon={ArrowRight} onClick={() => fromStopMenu(() => setShowSkipModal(true))}>
            Пропустить это место
          </MenuRow>
          {onBack && currentIndex > 0 && (
            <MenuRow icon={ArrowUUpLeft} onClick={() => fromStopMenu(() => setShowBackModal(true))}>
              Вернуться к предыдущему
            </MenuRow>
          )}
          {currentStop?.slug && (
            <MenuRow icon={Info} onClick={() => fromStopMenu(() => navigate(`/place/${currentStop.slug}`))}>
              Страница места
            </MenuRow>
          )}
          {menuExtra.map((item) => (
            <MenuRow
              key={item.label}
              icon={item.icon}
              danger={item.danger}
              onClick={() => fromStopMenu(item.onClick)}
            >
              {item.label}
            </MenuRow>
          ))}
        </div>
      </BottomSheet>

      <GeoModals geo={geo} />
      <GeoDebug geo={geo} />

      {/* Возврат снимает отметку с текущего места — спрашиваем подтверждение */}
      <ConfirmSheet
        isOpen={showBackModal}
        onClose={() => setShowBackModal(false)}
        title="Вернуться к предыдущему месту?"
        action="Вернуться"
        onAction={() => { telegramService.hapticFeedback('light'); setShowBackModal(false); onBack?.(); }}
      >
        Отметка с места {currentIndex} будет снята, и вы снова окажетесь
        на нём. Остальной прогресс сохранится.
      </ConfirmSheet>

      {/* Отметка издалека — предупреждение, а не запрет */}
      <BottomSheet
        isOpen={showFarModal}
        onClose={() => setShowFarModal(false)}
        title="Вы далеко от места"
        footer={
          <div className="flex gap-2">
            <button
              onClick={() => setShowFarModal(false)}
              className="flex-1 h-12 rounded-md text-label transition-colors duration-fast ease-out active:scale-98"
              style={{ background: 'var(--color-card-secondary)', color: 'var(--color-text-secondary)' }}
            >
              Ещё иду
            </button>
            <button
              onClick={markArrived}
              className="flex-1 h-12 rounded-md text-label bg-action text-on-action border-0 transition-colors duration-fast ease-out active:scale-98"
            >
              Всё равно отметить
            </button>
          </div>
        }
      >
        <p className="text-body-sm text-theme-text-secondary">
          По геолокации до места ещё {distanceLabel}. Если вы уже на месте —
          отметьте, гео иногда ошибается.
        </p>
      </BottomSheet>

      {/* Синяя, а не красная: пропуск ничего не стирает */}
      <ConfirmSheet
        isOpen={showSkipModal}
        onClose={() => setShowSkipModal(false)}
        title="Пропустить это место?"
        action="Пропустить"
        onAction={() => { telegramService.hapticFeedback('light'); setShowSkipModal(false); onSkip(); }}
      >
        Место будет отмечено как пропущенное, и вы перейдёте к следующему.
      </ConfirmSheet>
    </div>
  );
};

export { ConfirmSheet };
export default WalkScreen;
